import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Tab definitions ────────────────────────────────────────────────────────
// NOTE: "GTM Template" tab (submission process) is Velaro-internal — saved to OneDrive.
// Customers see "Template Gallery" (how to install from the gallery).

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "events", label: "Events Reference" },
  { id: "variables", label: "Variable Passing" },
  { id: "gallery", label: "Template Gallery" },
  { id: "test", label: "Test & Verify" }
];

// ─── Code block helper ──────────────────────────────────────────────────────

function CodeBlock({ code, lang = "js" }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="relative rounded-lg overflow-hidden border border-slate-200 mb-4">
      <div className="flex items-center justify-between bg-slate-800 px-4 py-2">
        <span className="text-xs text-slate-400 font-mono">{lang}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-slate-300 hover:text-white"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="bg-slate-900 text-slate-100 text-xs p-4 overflow-x-auto leading-relaxed whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

// ─── Info callout ───────────────────────────────────────────────────────────

function InfoBox({ children, type = "blue" }: { children: React.ReactNode; type?: "blue" | "amber" | "green" | "violet" }) {
  const styles = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    green: "bg-green-50 border-green-200 text-green-800",
    violet: "bg-violet-50 border-violet-200 text-violet-800"
  };
  return (
    <div className={`rounded-lg border p-4 text-sm mb-4 ${styles[type]}`}>
      {children}
    </div>
  );
}

// ─── Tab: Overview & Benefits ───────────────────────────────────────────────

function TabOverview() {
  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800 mb-2">Why use Velaro with Google Tag Manager?</h3>
        <p className="text-slate-600 text-sm">
          Google Tag Manager lets you deploy and manage Velaro's chat widget without touching your codebase.
          You also get access to Velaro's full event stream — chat started, conversation resolved, bot escalated —
          which you can push to GA4, Ads, Meta Pixel, and any other tag in your container.
        </p>
      </div>

      {/* GA4 tip — prominent */}
      <InfoBox type="violet">
        <strong>📊 Using Google Analytics 4?</strong> You don't need a separate GA4 integration.
        Install GA4 via GTM (using Google's official <em>Google Tag</em> or <em>GA4 Configuration</em> tag),
        then add the Velaro event listener tag in the same container. All Velaro events flow into GA4 automatically —
        one container, zero duplicate tags.
      </InfoBox>

      {/* Benefit cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {[
          {
            icon: "⚡",
            title: "Zero-code widget deploy",
            desc: "Paste one GTM container snippet and the Velaro widget deploys instantly. No developer required for future changes."
          },
          {
            icon: "📊",
            title: "Chat events → GA4",
            desc: "Every Velaro interaction (chat_started, survey_submitted, appointment_booked, etc.) fires as GA4 custom events automatically."
          },
          {
            icon: "🎯",
            title: "Ad conversion tracking",
            desc: "Fire Google Ads and Meta Pixel conversion events when a chat converts — measure ROI of paid campaigns on live chat."
          },
          {
            icon: "👤",
            title: "Visitor data into the bot",
            desc: "Push page variables (logged-in user name, email, plan tier, order ID) from GTM into the Velaro bot context."
          },
          {
            icon: "🛒",
            title: "E-commerce attribution",
            desc: "See which products, categories, and cart values lead to chats. Combine with Shopify for full attribution."
          },
          {
            icon: "🔁",
            title: "Retargeting audiences",
            desc: "Build GA4 audiences of visitors who started chats but didn't convert — retarget them with paid ads."
          }
        ].map((item) => (
          <div key={item.title} className="rounded-xl border border-slate-200 p-4">
            <div className="text-2xl mb-2">{item.icon}</div>
            <div className="font-semibold text-slate-800 text-sm mb-1">{item.title}</div>
            <p className="text-xs text-slate-500">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Quick start */}
      <div className="rounded-xl border border-slate-200 p-5 mb-4">
        <h4 className="font-bold text-slate-800 mb-3">Quick start — manual install (5 minutes)</h4>
        <ol className="space-y-2 text-sm text-slate-700">
          {[
            "In GTM, create a new Custom HTML tag.",
            "Paste the Velaro embed snippet (from Messaging → Channels → Widget → Embed Code).",
            "Set the trigger to \"All Pages\" or your desired pages.",
            "Publish the container — Velaro widget is live.",
            "Optional: add Velaro event listener tags to push events to GA4, Ads, or Meta Pixel."
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      <InfoBox type="green">
        <strong>🏪 One-click install available:</strong> Search for <strong>"Velaro"</strong> in the
        GTM Template Gallery (GTM → Templates → Search Gallery) to install the official Velaro tag
        in one click — no Custom HTML required. See the <em>Template Gallery</em> tab for details.
      </InfoBox>
    </div>
  );
}

// ─── Tab: Events Reference ──────────────────────────────────────────────────

const VELARO_EVENTS = [
  {
    event: "velaro_widget_opened",
    trigger: "Visitor clicks the chat bubble (before sending any message)",
    ga4use: "Engagement metric — how many visitors open the widget vs. start a chat",
    properties: [
      { name: "event", type: "string", example: "velaro_widget_opened" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_page_url", type: "string", example: "https://yourstore.com/cart" }
    ]
  },
  {
    event: "velaro_chat_started",
    trigger: "Visitor opens the chat widget and sends the first message",
    ga4use: "Top-of-funnel conversion — track as GA4 conversion event to measure chat adoption",
    properties: [
      { name: "event", type: "string", example: "velaro_chat_started" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_page_url", type: "string", example: "https://yourstore.com/products/shoes" },
      { name: "velaro_referrer", type: "string", example: "https://google.com" }
    ]
  },
  {
    event: "velaro_bot_escalated",
    trigger: "Bot could not answer and handed off to a human agent",
    ga4use: "Bot quality metric — high escalation rate = bot needs more training",
    properties: [
      { name: "event", type: "string", example: "velaro_bot_escalated" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_escalation_reason", type: "string", example: "no_answer | user_requested | sentiment" }
    ]
  },
  {
    event: "velaro_lead_captured",
    trigger: "Bot collected visitor email or phone via a form or workflow node",
    ga4use: "Lead gen conversion — fire a Google Ads conversion when email is captured",
    properties: [
      { name: "event", type: "string", example: "velaro_lead_captured" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_lead_type", type: "string", example: "email | phone | both" }
    ]
  },
  {
    event: "velaro_appointment_booked",
    trigger: "Bot confirmed a calendar booking (Outlook Calendar or Calendly integration)",
    ga4use: "Highest-value conversion — map to Google Ads as a primary conversion with revenue value",
    properties: [
      { name: "event", type: "string", example: "velaro_appointment_booked" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_booking_type", type: "string", example: "calendly | outlook" }
    ]
  },
  {
    event: "velaro_conversation_resolved",
    trigger: "Conversation is closed by agent or bot (with or without CSAT)",
    ga4use: "Support efficiency — combined with duration_seconds to measure resolution time",
    properties: [
      { name: "event", type: "string", example: "velaro_conversation_resolved" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_resolution_type", type: "string", example: "bot | agent | timeout" },
      { name: "velaro_csat_score", type: "number | null", example: "5" },
      { name: "velaro_message_count", type: "number", example: "8" },
      { name: "velaro_duration_seconds", type: "number", example: "143" }
    ]
  },
  {
    event: "velaro_survey_submitted",
    trigger: "Visitor completes a post-chat, pre-chat, or NPS survey",
    ga4use: "Satisfaction tracking — send NPS/CSAT scores as GA4 custom dimensions for cohort analysis",
    properties: [
      { name: "event", type: "string", example: "velaro_survey_submitted" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_session_id", type: "string", example: "sess_abc123" },
      { name: "velaro_survey_type", type: "string", example: "prechat | postchat | nps" },
      { name: "velaro_nps_score", type: "number | null", example: "9" },
      { name: "velaro_csat_score", type: "number | null", example: "5" }
    ]
  },
  {
    event: "velaro_proactive_chat_triggered",
    trigger: "A proactive chat rule fired and displayed an invitation to the visitor",
    ga4use: "Proactive engagement — compare velaro_proactive_chat_triggered vs velaro_chat_started to measure acceptance rate",
    properties: [
      { name: "event", type: "string", example: "velaro_proactive_chat_triggered" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_page_url", type: "string", example: "https://yourstore.com/pricing" },
      { name: "velaro_rule_name", type: "string", example: "Pricing page — 15s" }
    ]
  },
  {
    event: "velaro_offline_form_submitted",
    trigger: "Visitor submitted the unavailable survey (no agents online)",
    ga4use: "After-hours lead capture — fire an Ads conversion so you still get credit for off-hours leads",
    properties: [
      { name: "event", type: "string", example: "velaro_offline_form_submitted" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_page_url", type: "string", example: "https://yourstore.com/contact" }
    ]
  },
  {
    event: "velaro_widget_closed",
    trigger: "Visitor closes the chat widget without completing a conversation",
    ga4use: "Drop-off analysis — combine with velaro_widget_opened to measure widget abandonment rate by page",
    properties: [
      { name: "event", type: "string", example: "velaro_widget_closed" },
      { name: "velaro_site_id", type: "number", example: "12345" },
      { name: "velaro_page_url", type: "string", example: "https://yourstore.com/checkout" },
      { name: "velaro_had_conversation", type: "boolean", example: "false" }
    ]
  }
];

// ─── Recommended GA4 conversion setup ───────────────────────────────────────

const GA4_CONVERSIONS = [
  { event: "velaro_lead_captured", priority: "High", why: "Email/phone captured = qualified lead. Map to Google Ads as a secondary conversion." },
  { event: "velaro_appointment_booked", priority: "Critical", why: "Confirmed booking = revenue intent. Map to Ads as primary conversion with estimated value." },
  { event: "velaro_chat_started", priority: "Medium", why: "Top-of-funnel engagement. Use for audience building and remarketing." },
  { event: "velaro_survey_submitted", priority: "Medium", why: "Post-chat survey completion indicates satisfied customer. Good for loyalty cohorts." },
  { event: "velaro_offline_form_submitted", priority: "High", why: "Off-hours lead — still a real conversion, don't lose attribution." }
];

function TabEvents() {
  const [expanded, setExpanded] = useState<string | null>("velaro_chat_started");

  const listenerCode = `// Add this ONCE to your GTM container — Custom HTML tag, trigger: All Pages
// This single tag captures ALL Velaro events and pushes them to dataLayer

window.addEventListener("message", function(e) {
  if (!e.data || e.data.source !== "velaro") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: e.data.event,
    velaro_site_id: e.data.siteId,
    velaro_session_id: e.data.sessionId,
    velaro_page_url: e.data.pageUrl,
    velaro_referrer: e.data.referrer,
    velaro_resolution_type: e.data.resolutionType,
    velaro_csat_score: e.data.csatScore,
    velaro_nps_score: e.data.npsScore,
    velaro_survey_type: e.data.surveyType,
    velaro_message_count: e.data.messageCount,
    velaro_duration_seconds: e.data.durationSeconds,
    velaro_escalation_reason: e.data.escalationReason,
    velaro_lead_type: e.data.leadType,
    velaro_booking_type: e.data.bookingType,
    velaro_rule_name: e.data.ruleName,
    velaro_had_conversation: e.data.hadConversation
  });
}, false);`;

  return (
    <div>
      <p className="text-slate-600 text-sm mb-4">
        Velaro fires events via <code className="bg-slate-100 px-1 rounded text-xs">window.postMessage</code>.
        Install the listener tag below to capture all events and push them into <code className="bg-slate-100 px-1 rounded text-xs">window.dataLayer</code>,
        where GTM picks them up automatically. All events include <code className="bg-slate-100 px-1 rounded text-xs">source: "velaro"</code> for safe origin filtering.
      </p>

      <h4 className="font-semibold text-slate-800 mb-3">Step 1 — Install the event listener tag (one-time)</h4>
      <CodeBlock code={listenerCode} lang="javascript (Custom HTML tag — trigger: All Pages)" />

      {/* GA4 conversions recommendation */}
      <div className="rounded-xl border border-green-200 bg-green-50 p-4 mb-6">
        <div className="font-semibold text-green-800 mb-3">⭐ Recommended GA4 conversions to mark</div>
        <div className="space-y-2">
          {GA4_CONVERSIONS.map((c) => (
            <div key={c.event} className="flex items-start gap-3">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5 ${
                c.priority === "Critical" ? "bg-red-100 text-red-700" :
                c.priority === "High" ? "bg-amber-100 text-amber-700" :
                "bg-slate-100 text-slate-600"
              }`}>{c.priority}</span>
              <div>
                <code className="text-xs font-mono text-green-700">{c.event}</code>
                <p className="text-xs text-slate-600 mt-0.5">{c.why}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-green-700 mt-3">
          In GA4: Admin → Events → Mark as conversion. In Google Ads: link GA4 and import conversions.
        </p>
      </div>

      {/* Step 2 — GA4 Event tag setup */}
      <h4 className="font-semibold text-slate-800 mb-3 mt-6">Step 2 — Create GA4 Event tags in GTM</h4>
      <p className="text-slate-600 text-sm mb-4">
        Once the listener tag pushes events to <code className="bg-slate-100 px-1 rounded text-xs">dataLayer</code>,
        you need one GA4 Event tag per Velaro event to send it to GA4. Set up one for your most important event
        (e.g. <code className="bg-slate-100 px-1 rounded text-xs">velaro_chat_started</code>), then repeat for others.
      </p>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 mb-6">
        {[
          {
            step: "2a",
            label: "Create a Custom Event trigger",
            detail: "In GTM → Triggers → New → Trigger type: Custom Event. Set Event Name to the exact event (e.g. velaro_chat_started). Save the trigger."
          },
          {
            step: "2b",
            label: "Create a GA4 Event tag",
            detail: "In GTM → Tags → New → Tag type: Google Analytics: GA4 Event. Select your GA4 Configuration tag. Set Event Name to the same string (e.g. velaro_chat_started). Attach the trigger from 2a."
          },
          {
            step: "2c",
            label: "Add event parameters",
            detail: "In the GA4 Event tag, under \"Event Parameters\", add rows: Parameter Name = velaro_site_id → Value = {{DLV - velaro_site_id}} and repeat for velaro_session_id, velaro_page_url, etc. These become GA4 custom dimensions."
          },
          {
            step: "2d",
            label: "Register custom dimensions in GA4",
            detail: "In GA4 → Admin → Custom definitions → Create custom dimension. Set Dimension name (e.g. \"Velaro Session ID\"), Scope: Event, Event parameter: velaro_session_id. Repeat for each parameter you want to see in reports."
          },
          {
            step: "2e",
            label: "Mark as conversion (for key events)",
            detail: "In GA4 → Admin → Events → find velaro_lead_captured, velaro_appointment_booked, or velaro_chat_started → toggle \"Mark as conversion\". Then import into Google Ads: Ads → Conversions → Import from GA4."
          },
          {
            step: "2f",
            label: "Publish and verify in DebugView",
            detail: "Publish the GTM container. Open GA4 → Admin → DebugView. Trigger a chat on your site. Events appear in realtime — look for velaro_chat_started with all parameters populated."
          }
        ].map((item) => (
          <div key={item.step} className="px-4 py-4 flex items-start gap-4">
            <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">
              {item.step}
            </span>
            <div>
              <div className="font-medium text-slate-800 text-sm mb-1">{item.label}</div>
              <p className="text-xs text-slate-500">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>

      <InfoBox type="blue">
        <strong>One tag, many events:</strong> You can also use a single GA4 Event tag with a trigger that fires on <em>all</em> Velaro events —
        set the trigger to Custom Event with <code>velaro_.*</code> (regex match), and set Event Name to{" "}
        <code>{"{{Event}}"}</code> (built-in GTM variable). This sends all events to GA4 with one tag.
        Good for getting started fast; switch to individual tags when you need per-event parameter customization.
      </InfoBox>

      <h4 className="font-semibold text-slate-800 mb-3 mt-6">Step 3 — All events fired by Velaro</h4>
      <div className="space-y-2">
        {VELARO_EVENTS.map((ev) => (
          <div key={ev.event} className="rounded-xl border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setExpanded(expanded === ev.event ? null : ev.event)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
            >
              <div className="flex items-center gap-3">
                <code className="text-sm font-mono font-semibold text-blue-700">{ev.event}</code>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400 hidden sm:block">{ev.trigger}</span>
                <span className="text-slate-400">{expanded === ev.event ? "▲" : "▼"}</span>
              </div>
            </button>

            {expanded === ev.event && (
              <div className="border-t border-slate-100 px-4 py-3">
                <p className="text-xs text-slate-500 mb-2">{ev.trigger}</p>
                <div className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 mb-3">
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">GA4 use case:</span>
                  <span className="text-xs text-blue-700">{ev.ga4use}</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left pb-2 text-slate-500 font-medium">Property</th>
                      <th className="text-left pb-2 text-slate-500 font-medium">Type</th>
                      <th className="text-left pb-2 text-slate-500 font-medium">Example value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {ev.properties.map((prop) => (
                      <tr key={prop.name}>
                        <td className="py-1.5 font-mono text-slate-700">{prop.name}</td>
                        <td className="py-1.5 text-slate-400">{prop.type}</td>
                        <td className="py-1.5 font-mono text-slate-600">{prop.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-1 font-medium">GTM trigger condition (Custom Event):</p>
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded block">
                    Event Name equals <strong>{ev.event}</strong>
                  </code>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <InfoBox type="blue">
        <strong>Tip:</strong> In GTM, create one Custom Event trigger per Velaro event name, then attach
        each trigger to your GA4 Event tag. Use "Event equals velaro_appointment_booked" or
        "velaro_lead_captured" to track chats as GA4 conversions — then import them into Google Ads
        for ROAS reporting on paid campaigns that drove chat conversations.
      </InfoBox>
    </div>
  );
}

// ─── Tab: Variable Passing ──────────────────────────────────────────────────

function TabVariables() {
  const visitorVarCode = `// GTM Custom HTML tag — fires on pages where you know the visitor
// (e.g. after login, on account pages, in checkout flow)
window.__velaro = window.__velaro || {};
window.__velaro.visitor = {
  name: "{{DLV - user.name}}",        // GTM Data Layer Variable
  email: "{{DLV - user.email}}",
  phone: "{{DLV - user.phone}}",
  plan: "{{DLV - user.plan}}",        // e.g. "Growth", "Pro"
  customerId: "{{DLV - user.id}}",
  orderId: "{{DLV - ecommerce.orderId}}",  // e.g. from checkout page
  cartTotal: "{{DLV - ecommerce.value}}"
};`;

  const dlvPushCode = `// Push visitor data to dataLayer BEFORE GTM fires (e.g. in page source)
// Your website/backend should output this block for logged-in users:
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  "user.name": "Alex Johnson",
  "user.email": "alex@example.com",
  "user.plan": "Growth",
  "user.id": "cust_1234"
});`;

  const shopifyLiquidCode = `{% comment %} Add to theme.liquid, before GTM container snippet {% endcomment %}
{% if customer %}
<script>
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "user.name": "{{ customer.name | escape }}",
    "user.email": "{{ customer.email | escape }}",
    "user.id": "{{ customer.id }}"
  });
</script>
{% endif %}`;

  const wooCode = `<?php
// Add to functions.php — outputs visitor data before wp_footer
add_action('wp_footer', function() {
  if (is_user_logged_in()) {
    $user = wp_get_current_user();
    echo '<script>
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        "user.name": "' . esc_js($user->display_name) . '",
        "user.email": "' . esc_js($user->user_email) . '",
        "user.id": "' . esc_js($user->ID) . '"
      });
    </script>';
  }
}, 1); // priority 1 = before GTM fires`;

  return (
    <div>
      <p className="text-slate-600 text-sm mb-5">
        When Velaro loads, it reads <code className="bg-slate-100 px-1 rounded text-xs">window.__velaro.visitor</code> to
        pre-populate the visitor's name, email, and custom attributes into the bot context. Set this object from GTM
        (using Data Layer Variables) <em>before</em> the Velaro widget tag fires.
      </p>

      <h4 className="font-semibold text-slate-800 mb-2">The visitor object</h4>
      <CodeBlock code={visitorVarCode} lang="javascript (GTM Custom HTML tag — trigger: All Pages, sequence before Velaro widget)" />

      <InfoBox type="blue">
        <strong>Sequence matters:</strong> In your Velaro GTM tag settings, under "Advanced → Tag Sequencing",
        set the visitor variable tag to fire <em>before</em> the Velaro widget tag. This ensures the bot
        has the visitor data before the first message.
      </InfoBox>

      <h4 className="font-semibold text-slate-800 mb-2 mt-4">Push data from your website into dataLayer</h4>
      <CodeBlock code={dlvPushCode} lang="javascript (in your page source — fires before GTM)" />

      <h4 className="font-semibold text-slate-800 mb-2 mt-4">Shopify — Liquid template snippet</h4>
      <CodeBlock code={shopifyLiquidCode} lang="liquid (theme.liquid)" />

      <h4 className="font-semibold text-slate-800 mb-2 mt-4">WooCommerce — PHP snippet</h4>
      <CodeBlock code={wooCode} lang="php (functions.php)" />

      <h4 className="font-semibold text-slate-800 mb-3 mt-4">What the bot does with visitor data</h4>
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-4 py-3 text-left text-slate-600 font-semibold">Field</th>
              <th className="px-4 py-3 text-left text-slate-600 font-semibold">Effect in bot</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {[
              { field: "name", effect: "Bot greets visitor by name: \"Hi Alex, how can I help?\"" },
              { field: "email", effect: "Pre-filled in any email collection form. Not re-asked." },
              { field: "plan", effect: "Bot can answer plan-specific questions. Routing rules can branch by plan." },
              { field: "customerId", effect: "Passed to Shopify/WooCommerce skill — enables order lookups without re-asking for email." },
              { field: "orderId", effect: "Bot can pull live order status immediately on first message." },
              { field: "cartTotal", effect: "Used by abandoned cart recovery workflow to include cart value in follow-up." }
            ].map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                <td className="px-4 py-3 font-mono text-sm text-slate-700">{row.field}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{row.effect}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: Template Gallery ─────────────────────────────────────────────────
// Customer-facing: how to find and install the Velaro GTM template.
// Internal submission docs → OneDrive: gtm-template-submission-guide.md

function TabGallery() {
  const manualCode = `<!-- Paste in GTM → Tags → New → Custom HTML tag -->
<!-- Trigger: All Pages -->
<script>
  (function() {
    var s = document.createElement('script');
    s.src = 'https://cdn.velaro.com/widget/YOUR_SITE_ID/bundle.js';
    s.async = true;
    document.head.appendChild(s);
  })();
</script>`;

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800 mb-2">Installing Velaro via GTM</h3>
        <p className="text-slate-600 text-sm">
          There are two ways to install Velaro through Google Tag Manager — the one-click Template Gallery
          (no code) or a Custom HTML tag (manual, always works).
        </p>
      </div>

      {/* Option 1 — Template Gallery */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🏪</span>
          <div className="font-bold text-blue-800">Option 1 — GTM Template Gallery (one-click)</div>
          <span className="text-[10px] font-bold bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full">Recommended</span>
        </div>
        <ol className="space-y-2 text-sm text-blue-800 mb-4">
          {[
            "In GTM, go to Templates → Search Gallery.",
            "Search for \"Velaro\" and click the Velaro Live Chat template.",
            "Click Add to workspace.",
            "Create a new Tag → choose Velaro Live Chat from Custom templates.",
            "Enter your Site ID (from Channels → Widget → Embed Code).",
            "Set trigger to All Pages (or specific pages) and publish."
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <InfoBox type="blue">
          <strong>Template not appearing?</strong> The template may still be in review. Use Option 2 below in the meantime — it takes 2 minutes and works identically.
        </InfoBox>
      </div>

      {/* Option 2 — Manual */}
      <div className="rounded-xl border border-slate-200 p-5 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">✍️</span>
          <div className="font-bold text-slate-800">Option 2 — Custom HTML tag (manual)</div>
        </div>
        <ol className="space-y-2 text-sm text-slate-700 mb-4">
          {[
            "In GTM, go to Tags → New → Tag type: Custom HTML.",
            "Paste the embed code below (replace YOUR_SITE_ID with your actual Site ID).",
            "Set the trigger to All Pages.",
            "Save and publish the container."
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-600 text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <CodeBlock code={manualCode} lang="html (GTM Custom HTML tag)" />
        <p className="text-xs text-slate-500">
          Your Site ID is in <strong>Channels → Web Chat → your deployment → Embed Code</strong>.
        </p>
      </div>

      {/* GA4 note */}
      <InfoBox type="violet">
        <strong>📊 Also using Google Analytics 4?</strong> Add a second tag to your container using Google's
        official <em>Google Tag</em> template (search "Google Tag" in the gallery, enter your GA4 Measurement ID).
        Both tags fire on the same container — one publish deploys both Velaro and GA4, and Velaro events
        flow automatically into GA4 once you add the event listener tag (see Events Reference tab).
        <strong> No separate Google Analytics integration is needed.</strong>
      </InfoBox>

      {/* What the template includes */}
      <div className="rounded-xl border border-slate-200 p-5">
        <div className="font-semibold text-slate-800 mb-3">What the Velaro GTM template includes</div>
        <div className="space-y-2">
          {[
            { icon: "✓", label: "Widget embed", desc: "Automatically loads the correct widget bundle for your Site ID" },
            { icon: "✓", label: "Event listener", desc: "Pushes all Velaro events to dataLayer automatically — no extra tags needed" },
            { icon: "✓", label: "Visitor variable support", desc: "Optional checkbox to enable window.__velaro.visitor passing from dataLayer" },
            { icon: "✓", label: "Consent mode compatible", desc: "Respects Google Consent Mode v2 — Velaro loads after consent is granted" }
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <span className="text-green-500 font-bold shrink-0">{item.icon}</span>
              <div>
                <span className="font-medium text-slate-700 text-sm">{item.label}</span>
                <span className="text-slate-500 text-xs"> — {item.desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Test & Verify ─────────────────────────────────────────────────────

function TabTest() {
  const debugCode = `// Open Chrome DevTools console and run:

// 1. Check the dataLayer for Velaro events
window.dataLayer.filter(e => e.event && e.event.startsWith("velaro_"));

// 2. Verify visitor variables are set before the widget loads
console.log(window.__velaro?.visitor);

// 3. Manually fire a test event (simulates chat_started)
window.dataLayer.push({
  event: "velaro_chat_started",
  velaro_site_id: YOUR_SITE_ID,
  velaro_session_id: "test_session",
  velaro_page_url: window.location.href
});

// 4. Check all events from this session
window.dataLayer.filter(e => e.velaro_session_id);`;

  const checklist = [
    { label: "GTM container fires (check Network tab for gtm.js)", done: true },
    { label: "Velaro widget appears on page", done: true },
    { label: "No JS errors in console related to Velaro", done: true },
    { label: "window.__velaro.visitor is populated for logged-in users", done: false },
    { label: "velaro_widget_opened fires when chat bubble clicked", done: false },
    { label: "velaro_chat_started fires when first message sent", done: false },
    { label: "velaro_conversation_resolved fires when chat closes", done: false },
    { label: "Events appear in GA4 DebugView (realtime)", done: false },
    { label: "Google Ads conversion fires on velaro_lead_captured or velaro_appointment_booked", done: false }
  ];

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-slate-800 mb-2">Test & verify your setup</h3>
        <p className="text-slate-600 text-sm">
          Use GTM's Preview mode and Chrome DevTools to confirm events are firing correctly before
          publishing to production.
        </p>
      </div>

      <InfoBox type="amber">
        <strong>Always test in GTM Preview mode first.</strong> Click "Preview" in GTM, navigate to your site,
        and verify each tag fires on the correct trigger. Only publish once the Tag Assistant shows all green.
      </InfoBox>

      <h4 className="font-semibold text-slate-800 mb-2">DevTools verification script</h4>
      <CodeBlock code={debugCode} lang="javascript (Chrome DevTools console)" />

      <h4 className="font-semibold text-slate-800 mb-3 mt-4">Verification checklist</h4>
      <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
        {checklist.map((item, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3">
            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
              item.done ? "bg-green-500 border-green-500" : "border-slate-300"
            }`}>
              {item.done && <span className="text-white text-xs">✓</span>}
            </div>
            <span className={`text-sm ${item.done ? "text-slate-400 line-through" : "text-slate-700"}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 p-5">
        <h4 className="font-bold text-slate-800 mb-3">GA4 DebugView</h4>
        <ol className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold flex-shrink-0">1.</span>
            Open GA4 → Admin → DebugView
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold flex-shrink-0">2.</span>
            Install the "GA Debugger" Chrome extension (or use ?gtm_debug=x in the URL)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold flex-shrink-0">3.</span>
            Open your site with GTM Preview active and trigger a chat
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold flex-shrink-0">4.</span>
            DebugView shows events in realtime — look for <code className="bg-slate-100 px-1 rounded text-xs">velaro_chat_started</code>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold flex-shrink-0">5.</span>
            Verify all parameters are populated (site_id, session_id, page_url)
          </li>
        </ol>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function GoogleTagManager() {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl font-bold">
            GTM
          </div>
          <div>
            <h2 className="text-xl font-bold">Google Tag Manager</h2>
            <p className="text-blue-100 text-sm">Deploy Velaro, track chat events, pass visitor data — all without code</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          {[
            "Zero-code deploy",
            "10 chat events → GA4",
            "Ad conversion tracking",
            "Visitor variable passing",
            "Template Gallery"
          ].map((tag) => (
            <span key={tag} className="text-xs bg-white/15 px-3 py-1 rounded-full">{tag}</span>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === "overview" && <TabOverview />}
          {activeTab === "events" && <TabEvents />}
          {activeTab === "variables" && <TabVariables />}
          {activeTab === "gallery" && <TabGallery />}
          {activeTab === "test" && <TabTest />}
        </div>
      </div>
    </div>
  );
}

export function BackToIntegrationLandingGTM() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer items-center gap-2 text-slate-600"
      onClick={() => navigate("/integrations")}
    >
      ← Back to Integrations
    </div>
  );
}
